import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDatabase, FiServer, FiAlertTriangle, FiCheckCircle, FiX } = FiIcons;

const ConnectionChecker = ({ onClose }) => {
  const [checking, setChecking] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);
  const [databaseStatus, setDatabaseStatus] = useState(null);
  const [errorDetails, setErrorDetails] = useState('');

  const checkServerConnection = async () => {
    try {
      setChecking(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('/api/databases', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        setServerStatus('connected');
        setDatabaseStatus('connected');
      } else {
        setServerStatus('connected');
        setDatabaseStatus('error');
        const data = await response.json();
        setErrorDetails(data.error || 'Failed to connect to database');
      }
    } catch (err) {
      setServerStatus('error');
      setDatabaseStatus('unknown');
      setErrorDetails(err.name === 'AbortError' 
        ? 'Connection timeout. Make sure the server is running.'
        : err.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">Connection Diagnostics</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-700 mb-2">Connection Status</h4>
            <div className="space-y-4">
              {/* Server Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="mr-3 text-gray-600">
                    <SafeIcon icon={FiServer} className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Express Server</p>
                    <p className="text-xs text-gray-500">API Connected</p>
                  </div>
                </div>
                <div>
                  {serverStatus === 'connected' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <SafeIcon icon={FiCheckCircle} className="mr-1" /> Connected
                    </span>
                  )}
                  {serverStatus === 'error' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <SafeIcon icon={FiAlertTriangle} className="mr-1" /> Failed
                    </span>
                  )}
                  {serverStatus === null && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Not checked
                    </span>
                  )}
                </div>
              </div>
              
              {/* Database Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="mr-3 text-gray-600">
                    <SafeIcon icon={FiDatabase} className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">MySQL Database</p>
                    <p className="text-xs text-gray-500">129.146.189.183:3306</p>
                  </div>
                </div>
                <div>
                  {databaseStatus === 'connected' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <SafeIcon icon={FiCheckCircle} className="mr-1" /> Connected
                    </span>
                  )}
                  {databaseStatus === 'error' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <SafeIcon icon={FiAlertTriangle} className="mr-1" /> Failed
                    </span>
                  )}
                  {databaseStatus === 'unknown' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <SafeIcon icon={FiAlertTriangle} className="mr-1" /> Unknown
                    </span>
                  )}
                  {databaseStatus === null && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Not checked
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Error Details */}
          {errorDetails && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg">
              <h5 className="text-sm font-medium text-red-800 mb-1">Error Details</h5>
              <p className="text-xs text-red-700">{errorDetails}</p>
            </div>
          )}
          
          {/* Troubleshooting Tips */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-700 mb-2">Troubleshooting Tips</h4>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>Ensure the Express server is running with <code className="bg-gray-100 px-1 rounded">npm run server</code></li>
              <li>Verify MySQL server is running on 129.146.189.183:3306</li>
              <li>Check username (simarpreet) and password credentials</li>
              <li>Ensure MySQL user has proper permissions</li>
              <li>Check if there's a firewall blocking the connection</li>
            </ul>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={checkServerConnection}
              disabled={checking}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center"
            >
              {checking ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Checking...
                </>
              ) : (
                'Check Connection'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ConnectionChecker;